import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { T } from '@/lib/theme'
import { fmt, fmtUsd, fmtPct } from '@/lib/utils'

/// Keyboard-accessible clickable: make a non-<button> element (a sort header, a nav row, a menu item)
/// operable by keyboard. Spread onto the element: `<div {...clickable(handler)}>`. Adds role="button",
/// tabIndex, and Enter/Space activation. Use ONLY for genuine controls — never a modal backdrop or an
/// event-stop wrapper, and prefer a real <button>/<Link> when the layout allows one. Pass `{label}` to
/// set an aria-label when the element has no readable text.
export function clickable(handler, opts = {}) {
  const { disabled = false, label } = opts
  if (!handler) return {}
  return {
    onClick: disabled ? undefined : handler,
    onKeyDown: disabled ? undefined : (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(e) }
    },
    role: 'button',
    tabIndex: disabled ? -1 : 0,
    'aria-disabled': disabled || undefined,
    ...(label ? { 'aria-label': label } : null),
  }
}

/// Uniform dark-glass surface tone. `#0F1320` fill + noise, no coloured rim.
/// Semantic meaning lives in the TEXT (purple/emerald/gold/coral), not the chrome.
const PANEL_GLASS = {
  background: T.panelGlass.background,
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
}

/// The one canonical box (replaces the former Card + PanelWithRim - a single surface
/// implementation, so a style change can't land on one and miss the other). Base is a
/// flat dark page panel: `#0F1320` + noise, NO line border, a 1px top rim-light. Props
/// select the variants the two old components used to be:
///   elevated  lighter glass fill + soft shadow - a card that sits ABOVE the page (old <Card>)
///   hover     background-shift + 1px lift on pointer - for clickable cards
///   accent    left-edge 2px coloured rail - flags a CTA / category
/// `innerStyle` then `style` both still apply (drop-in for PanelWithRim's two-slot API).
/// PANEL_GLASS is referenced below its own declaration, which is safe: Panel only runs at
/// render time, long after the module has finished evaluating.
export function Panel({ children, style = {}, innerStyle = {}, id, elevated = false, hover = false, accent = null, ...rest }) {
  const surface = elevated
    ? { background: T.glassSurface, boxShadow: T.shadowMed }   // old Card: lighter fill, floats above
    : { ...PANEL_GLASS, boxShadow: 'none' }                    // old PanelWithRim: flat on black
  return (
    <div
      id={id}
      style={{
        position: 'relative',
        ...surface,
        border: 'none', // line border removed (founder call); edge = fill + shadow + top rim-light
        borderRadius: T.r.lg,
        padding: T.sp.lg,
        overflow: 'hidden',
        transition: 'background 0.2s ease, transform 0.2s ease',
        ...(accent ? { borderLeft: `2px solid ${accent}` } : null),
        ...(hover ? { cursor: 'pointer' } : null),
        ...innerStyle,
        ...style,
      }}
      onMouseEnter={hover ? (e) => {
        if (elevated) e.currentTarget.style.background = T.glassSurfaceHover
        e.currentTarget.style.transform = 'translateY(-1px)'
      } : undefined}
      onMouseLeave={hover ? (e) => {
        if (elevated) e.currentTarget.style.background = T.glassSurface
        e.currentTarget.style.transform = 'none'
      } : undefined}
      {...rest}
    >
      {/* 1-pixel highlight at the top edge - the glass "rim light". */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0) 100%)',
        pointerEvents: 'none',
      }} />
      {children}
    </div>
  )
}

/// Shared "inset" chrome for the small internal boxes (stat cells, hero cells) that used
/// to hand-roll `border + bg + padding` inline. One borderless token so they stay in sync
/// and match the borderless panels; spread it and add per-use `padding`:
///   `style={{ ...insetBox, padding: '12px 14px' }}`.
export const insetBox = {
  borderRadius: T.r.lg,
  background: 'rgba(255,255,255,0.02)',
}

export function SectionLabel({ children, style = {} }) {
  return (
    <div style={{
      fontSize: T.fs.caption,
      color: T.textDim,
      fontFamily: T.fontM,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      ...style,
    }}>{children}</div>
  )
}

/// label + value (+ optional sub) - the one stat-cell shape every dashboard section needs,
/// parametrized for the handful of chrome variants that were previously five near-identical
/// local components (StatCell/StreamStat/HeroCard/RowStat/HeroStat) scattered across
/// StakeSection/FarmSection/MarketsSection/PortfolioSection/EarnSection/ShareCard.
///   - size: value font size - 'sm' (T.fs.ui), 'cell' (18, FarmSection/MarketsSection/
///     StakeSection's old divider/stream cells), 'md' (20, default), 'heading'
///     (T.fs.heading, PortfolioSection's old HeroCard), 'lg' (T.fs.display, the big
///     portfolio numbers). Ignored when `boxed` is set - the boxed hero cells are always 26px.
///   - boxed: wraps in the bordered/padded/centered "hero cell" chrome (StakeSection's old
///     StatCell / EarnSection's old HeroStat).
///   - divider: adds a left hairline + grid-cell padding, for a row of cells inside one
///     panel (FarmSection/MarketsSection's old StatCell `divider` prop).
export function Stat({ label, value, sub, size = 'md', color = T.text, align = 'left', boxed = false, divider = false }) {
  const valueSize = boxed ? 26 : ({ sm: T.fs.ui, cell: 18, md: 20, heading: T.fs.heading, lg: T.fs.display }[size] ?? 20)
  // `divider` alone used to gate BOTH the left border AND the cell's own padding, which left
  // the first cell in every bordered "cell" row (no divider needed - it's the leftmost) with
  // zero padding while its siblings got 12px 14px, flush against the strip's own edge instead
  // of matching them. `size === 'cell'` is a reliable "this Stat lives in a bordered row"
  // signal on its own (every current cell-row usage sets it, no standalone/gap-spaced Stat
  // does) - so padding now applies to every cell in the row, and `divider` only adds the border.
  const boxStyle = boxed
    ? { ...insetBox, padding: '24px 26px' }
    : size === 'cell'
      ? { padding: '12px 14px', ...(divider ? { borderLeft: `1px solid ${T.border}` } : {}) }
      : {}
  return (
    <div style={{ textAlign: boxed ? 'center' : align, ...boxStyle }}>
      <SectionLabel>{label}</SectionLabel>
      <div className="mono" style={{
        fontSize: valueSize,
        fontWeight: 700,
        fontFamily: T.fontN,
        color,
        lineHeight: 1.1,
        marginTop: boxed ? 10 : size === 'lg' ? 8 : 4,
        letterSpacing: size === 'lg' ? '-0.02em' : '-0.01em',
        // Subtle text shadow on the big/coloured numbers gives the
        // "backlit" feel on the frosted surface.
        textShadow: size === 'lg' && color !== T.text
          ? `0 0 22px ${color}44`
          : 'none',
      }}>{value}</div>
      {sub != null && (
        <div className="mono" style={{
          fontSize: 11,
          color: T.textDim,
          fontFamily: T.fontN,
          marginTop: 3,
        }}>{sub}</div>
      )}
    </div>
  )
}

export function Row({ label, value, color, dim = false, bold = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: dim ? T.textDim : T.textMuted, fontSize: T.fs.body, fontFamily: T.fontM }}>{label}</span>
      <span className="mono" style={{ color: color || T.text, fontWeight: bold ? 800 : 600, fontFamily: T.fontN, fontSize: T.fs.body }}>{value}</span>
    </div>
  )
}

/// Premium button. Primary = gold glass with a subtle specular sheen.
/// Ghost = frosted panel with light border. Danger = coral fill.
/// One row per variant, so a variant either EXISTS here or throws a visible fallback - it can no
/// longer acquire an appearance by falling through someone else's ternary.
///
/// That is not hypothetical. `variant="secondary"` was used at four call sites and was never a
/// branch in this component: it landed on the glass background with a TRANSPARENT border, no blur,
/// and - because the boxShadow chain ended on the danger case - the destructive red glow. Four
/// buttons wore the delete-button shadow for months because a chain of ternaries had no final
/// `else` that meant anything. A lookup table cannot express that bug.
const VARIANTS = {
  // Two shadows doing two different jobs, which is why the offsets differ. The COLOURED one is a
  // glow: light coming off the surface itself, so it radiates evenly and takes a zero offset. The
  // BLACK one is a cast shadow: it implies a light source above, so it sits below the button. They
  // were previously fused into one bottom-offset coloured shadow, which read as the button leaking
  // downward rather than glowing.
  primary: {
    bg: `linear-gradient(180deg, ${T.accentLight} 0%, ${T.accent} 55%, ${T.accentDeep} 100%)`,
    color: '#08111F',
    border: 'transparent',
    shadow: '0 0 20px rgba(91, 158, 255, 0.32), 0 3px 10px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.35)',
    blur: false,
  },
  // The genuine alternative action: present enough to look clickable beside a primary, quiet enough
  // that it never competes to be the thing you press.
  secondary: {
    bg: T.glassButton,
    color: T.text,
    border: T.border,
    shadow: '0 4px 12px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.05)',
    blur: false,
  },
  // Lowest emphasis. Same surface as secondary but blurred and border-led rather than filled.
  ghost: {
    bg: T.glassButton,
    color: T.text,
    border: T.border,
    shadow: '0 4px 12px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.05)',
    blur: true,
  },
  danger: {
    bg: T.negative,
    color: '#08111F',
    border: 'transparent',
    shadow: '0 0 18px rgba(248,113,113,0.28), 0 3px 10px rgba(0,0,0,0.28)',
    blur: false,
  },
}

export function Button({ children, onClick, disabled, variant = 'primary', style = {}, ...rest }) {
  const v = VARIANTS[variant] ?? VARIANTS.secondary
  const isPrimary = variant === 'primary'
  const isGhost   = variant === 'ghost'
  // Everything that is neither the headline action nor destructive shares one hover behaviour.
  const isQuiet   = variant === 'ghost' || variant === 'secondary' || !VARIANTS[variant]

  const baseBg = disabled ? 'rgba(120,130,160,0.12)' : v.bg
  const baseColor = disabled ? T.textDim : v.color
  const baseBorder = v.border

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        position: 'relative',
        background: baseBg,
        color: baseColor,
        border: `1px solid ${disabled ? T.border : baseBorder}`,
        borderRadius: T.r.md,
        padding: '10px 16px',
        fontFamily: T.fontM,
        fontSize: T.fs.ui,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.18s ease',
        backdropFilter: v.blur ? T.glassBlur : 'none',
        WebkitBackdropFilter: v.blur ? T.glassBlur : 'none',
        boxShadow: disabled ? 'none' : v.shadow,
        overflow: 'hidden',
        ...style,
      }}
      onMouseEnter={disabled ? undefined : (e) => {
        if (isPrimary) {
          e.currentTarget.style.boxShadow = '0 0 32px rgba(91, 158, 255, 0.52), 0 4px 14px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.45)'
          e.currentTarget.style.filter = 'brightness(1.05)'
        } else if (isQuiet) {
          e.currentTarget.style.background = T.glassButtonHover
          e.currentTarget.style.borderColor = T.borderHover
        }
      }}
      onMouseLeave={disabled ? undefined : (e) => {
        if (isPrimary) {
          e.currentTarget.style.boxShadow = VARIANTS.primary.shadow
          e.currentTarget.style.filter = 'none'
        } else if (isQuiet) {
          e.currentTarget.style.background = v.bg
          e.currentTarget.style.borderColor = T.border
        }
      }}
      {...rest}
    >
      {/* Specular top highlight - the "glass pill" look on primary. */}
      {isPrimary && !disabled && (
        <span style={{
          position: 'absolute', top: 0, left: 8, right: 8, height: 1,
          background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 100%)',
          pointerEvents: 'none',
        }} />
      )}
      {children}
    </button>
  )
}

/// Percent-of-max amount slider for deposit/borrow/withdraw/repay forms. Works entirely in wei
/// so it never round-trips through a lossy Number() (same precision-safety principle as
/// fmtBalanceWei) - dragging to 100% yields the exact max wei, not a rounded-down float copy.
/// `onChangeWei` receives the computed wei amount; callers feed it into whatever input-string
/// state they already have via weiToInputString, same as every existing MAX button.
export function AmountSlider({ valueWei, maxWei, onChangeWei, disabled = false }) {
  const hasMax = !!maxWei && maxWei > 0n
  const pct = hasMax ? Math.min(100, Number(((valueWei ?? 0n) * 1000n) / maxWei) / 10) : 0
  const isDisabled = disabled || !hasMax

  function handleDrag(e) {
    if (!hasMax) return
    const p = Number(e.target.value)
    onChangeWei((maxWei * BigInt(Math.round(p * 10))) / 1000n)
  }

  return (
    <div style={{ marginTop: 12 }}>
      <input
        type="range" className="amount-slider" min={0} max={100} step={0.5}
        value={pct} onChange={handleDrag} disabled={isDisabled}
        style={{ background: `linear-gradient(to right, ${T.accent} 0%, ${T.accent} ${pct}%, rgba(255,255,255,0.08) ${pct}%, rgba(255,255,255,0.08) 100%)` }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        {[25, 50, 75, 100].map((p) => (
          <button
            key={p} type="button" disabled={isDisabled}
            onClick={() => hasMax && onChangeWei((maxWei * BigInt(p)) / 100n)}
            style={{ ...quickPctBtn, opacity: isDisabled ? 0.4 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
          >{p === 100 ? 'MAX' : `${p}%`}</button>
        ))}
      </div>
    </div>
  )
}

const quickPctBtn = {
  flex: 1, padding: '6px 4px', borderRadius: T.r.sm, cursor: 'pointer',
  border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)',
  color: T.textMuted, fontFamily: T.fontM, fontSize: 11, fontWeight: 700,
}

export function Input({ value, onChange, placeholder, suffix, type = 'text', style = {} }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      background: T.bg2,
      backdropFilter: T.glassBlur,
      WebkitBackdropFilter: T.glassBlur,
      border: `1px solid ${T.border}`,
      borderRadius: T.r.md,
      padding: '10px 12px',
      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
      transition: 'border-color 0.15s ease',
      ...style,
    }}
      onFocusCapture={(e) => { e.currentTarget.style.borderColor = T.borderHover }}
      onBlurCapture={(e)  => { e.currentTarget.style.borderColor = T.border }}
    >
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: T.text,
          fontFamily: T.fontN,
          fontSize: T.fs.ui,
        }}
      />
      {suffix && (
        <span style={{
          color: T.textDim,
          fontSize: T.fs.caption,
          fontFamily: T.fontM,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}>{suffix}</span>
      )}
    </div>
  )
}

/// Minimal shimmer skeleton for initial-load states. Pairs with the
/// global `@keyframes shimmer` in index.css via the `.skeleton` class.
/// Use inside a Stat or cell when `isLoading && !data` so users don't
/// see misleading "0.00" values while the first fetch is in flight.
export function Skeleton({ width = 80, height = 14, style = {} }) {
  return (
    <span
      className="skeleton"
      aria-busy="true"
      aria-label="loading"
      style={{
        display: 'inline-block',
        width,
        height,
        verticalAlign: 'middle',
        borderRadius: 4,
        ...style,
      }}
    />
  )
}

/// A "to be distributed / your reward" row for a permissionless keeper-crank panel - a big USD
/// figure with a USDx-equivalent subline, a help icon explaining the estimate, and an optional
/// `highlight` (positive-colored) treatment for the caller's own cut. First built for
/// FarmSection's "Distribute Fees" card, reused as-is by MarketsSection's "Distribute stETH
/// Yield" card - same shape, different harvest function underneath.
export function DistributeRow({ label, help, usdValue, highlight = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 600 }}>{label}</span>
        <HelpIcon text={help} />
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="mono" style={{ color: highlight ? T.positive : T.text, fontFamily: T.fontN, fontSize: 22, fontWeight: 800 }}>
          {fmtUsd(usdValue, usdValue < 1 ? 4 : 2)}
        </div>
        <div className="mono" style={{ color: T.textDim, fontFamily: T.fontN, fontSize: 11, marginTop: 2 }}>
          ≈ {fmt(usdValue, 4)} USDx
        </div>
      </div>
    </div>
  )
}

/// `text` is both the native browser tooltip and the accessible name. Set `nativeTitle={false}`
/// when this sits inside a `Tooltip` (below): that component renders its own styled panel from the
/// same string, and leaving `title` set makes the browser draw a second, unstyled copy right next
/// to it. `aria-label` is kept either way, so suppressing the visual duplicate costs no
/// accessibility - a screen reader still announces the icon.
export function HelpIcon({ text, nativeTitle = true }) {
  return (
    <span
      title={nativeTitle ? text : undefined}
      aria-label={text}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 14, height: 14, borderRadius: '50%', border: `1px solid ${T.textDim}`,
        color: T.textDim, fontSize: 9, fontFamily: T.fontM, fontWeight: 700,
        cursor: 'help', flexShrink: 0,
      }}
    >?</span>
  )
}

/// Hover-triggered floating info panel, glass-chrome styled to match the rest of the app - unlike
/// HelpIcon's native `title` (browser-default look, single-line, can't hold structured content),
/// this renders real markup, so a caller can show a small label/value breakdown on hover instead
/// of one plain sentence. `children` is the trigger element (a HelpIcon, an icon, a stat - whatever
/// should be hovered), `content` is what appears above it.
/// Portaled to document.body with FIXED positioning, measured off the trigger at open. The old
/// version positioned the bubble absolutely inside its parent, which worked everywhere until a
/// tooltip lived inside a scroll container: `overflow-x: auto` forces vertical clipping too (per
/// spec, the other axis computes to auto), so the Leaderboard's header tooltips opened upward past
/// the table wrapper's top edge and rendered as a clipped sliver. A portal escapes every clip
/// context, permanently, for every current and future call site. Closes on scroll rather than
/// tracking the trigger - simpler, and a moving tooltip is worse than a dismissed one.
export function Tooltip({ children, content, style = {} }) {
  const [pos, setPos] = useState(null)
  const ref = useRef(null)

  const open = () => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    // Clamp the bubble's center so it stays inside the viewport with a small margin - the arrow
    // stays centered on the bubble, which drifts off the trigger only at the extreme edges.
    const x = Math.min(Math.max(r.left + r.width / 2, 100), window.innerWidth - 100)
    setPos({ x, y: r.top - 9 })
  }
  const close = () => setPos(null)

  useEffect(() => {
    if (!pos) return
    window.addEventListener('scroll', close, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', close, { capture: true })
  }, [pos])

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex', ...style }}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {children}
      {pos && createPortal(
        <div style={{
          position: 'fixed', left: pos.x, top: pos.y, transform: 'translate(-50%, -100%)',
          zIndex: 1000, minWidth: 170, maxWidth: 300, padding: '10px 12px', borderRadius: T.r.md,
          background: 'rgba(10,14,26,0.97)', backdropFilter: T.glassBlur, WebkitBackdropFilter: T.glassBlur,
          border: `1px solid ${T.border}`, boxShadow: T.shadowMed,
          pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          {content}
          <div style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: '5px solid rgba(10,14,26,0.97)',
          }} />
        </div>,
        document.body,
      )}
    </span>
  )
}

export function Badge({ children, color = T.accent, style = {} }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '3px 10px',
      borderRadius: T.r.pill,
      background: `${color}14`,
      backdropFilter: T.glassBlur,
      WebkitBackdropFilter: T.glassBlur,
      border: `1px solid ${color}55`,
      color,
      fontSize: T.fs.caption,
      fontFamily: T.fontM,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      boxShadow: `0 0 12px ${color}22, inset 0 1px 0 rgba(255,255,255,0.05)`,
      ...style,
    }}>{children}</span>
  )
}

/// The small "MAX" text-button next to an amount input's balance readout - byte-identical
/// `maxBtn` style object used to be hand-defined separately in both StakeSection and
/// MarketsSection. The equivalent input-box/toggle-tab styling in those files (and EarnSection/
/// FarmSection) was checked too but genuinely differs per file - different padding, font sizes,
/// even DOM shape - so it stays local rather than being forced through a shared component that
/// would silently change how those pages look.
export function MaxLink({ onClick, children = 'MAX' }) {
  return (
    <button onClick={onClick} style={{
      marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0,
      color: T.accent, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, letterSpacing: '0.04em',
    }}>{children}</button>
  )
}

/// Horizontal progress bar - label, a track + fill, and a value on the right. Deliberately dumb:
/// callers pass an already-decided `color` rather than this component guessing risk semantics
/// itself - a plain utilization number isn't automatically "dangerous" the way LTV-vs-liquidation
/// is (see `ltvZone` in lib/utils.js for that specific, risk-flavored coloring, used by the Borrow
/// page's own health bar) - so callers that DO want that read pass the zone's color in explicitly.
export function ProgressBar({ label, value, valueLabel, max = 100, color = T.accent, sub, style = {} }) {
  const fillPct = Math.max(0, Math.min(100, (value / max) * 100))
  return (
    <div style={style}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{
          color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>{label}</span>
        <span className="mono" style={{ color, fontFamily: T.fontN, fontSize: T.fs.caption, fontWeight: 700 }}>
          {valueLabel ?? fmtPct(value)}
        </span>
      </div>
      <div style={{ position: 'relative', height: 8, borderRadius: T.r.pill, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${fillPct}%`, borderRadius: T.r.pill, background: color, transition: 'width 0.3s ease' }} />
      </div>
      {sub && <div style={{ marginTop: 4, color: T.textDim, fontFamily: T.fontM, fontSize: 11 }}>{sub}</div>}
    </div>
  )
}

/// Stacked horizontal bar + legend, for showing how a total splits across a few named shares (e.g.
/// protocol revenue) - segments sized by `pct` (0-100, expected to sum to <=100), each carrying its
/// own label/color. Reads far faster than a column of plain label:value rows for exactly this
/// shape of data - relative proportion is the point, not precise lookup. A zero-`pct` segment is
/// dropped from both the bar and the legend rather than shown as a redundant "X 0.00%" row -
/// pass every possible destination in unconditionally and let real values decide what's worth
/// showing, rather than each call site hand-filtering its own segments array.
export function DistributionBar({ segments }) {
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: T.r.pill, overflow: 'hidden', background: 'rgba(255,255,255,0.06)' }}>
        {segments.filter((s) => s.pct > 0).map((s) => (
          <div key={s.label} style={{ width: `${s.pct}%`, background: s.color }} />
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {segments.filter((s) => s.pct > 0).map((s) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption }}>{s.label}</span>
            <span className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.caption, fontWeight: 700 }}>{fmtPct(s.pct)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/// Centred overlay dialog, used for the quick Supply/Borrow path from the Markets list.
///
/// Closes on backdrop click and on Escape, and restores focus and page scroll on unmount. The
/// Escape handler matters more than it looks: a modal that traps someone with only a small ✕ to
/// find is the kind of thing that reads as broken on a phone, where the ✕ is the hardest target on
/// the screen.
///
/// Deliberately NOT a portal. This app renders inside one `AppShell` div with a fixed background
/// layer behind it and nothing that establishes a competing stacking context above `zIndex: 1`, so
/// a fixed-position overlay reaches the top of the viewport on its own. A portal would buy
/// isolation from a containing-block problem that does not exist here and cost the ErrorBoundary
/// its coverage of what is inside.
export function Modal({ open, onClose, title, children, width = 460 }) {
  useEffect(() => {
    if (!open) return undefined
    function onKey(e) { if (e.key === 'Escape') onClose?.() }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        background: 'rgba(4, 7, 16, 0.82)', backdropFilter: 'none',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{ width: '100%', maxWidth: width, margin: 'auto' }}
      >
        <div style={{ position: 'relative' }}>
          <button onClick={onClose} aria-label="Close" style={{
            position: 'absolute', top: -14, right: -14, zIndex: 1,
            width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
            border: `1px solid ${T.border}`, background: T.bg2, color: T.textMuted, fontSize: 15,
          }}>✕</button>
          {children}
        </div>
      </div>
    </div>
  )
}

/// Segmented toggle: two or three mutually exclusive modes filling one row (Stake/Unstake,
/// Supply/Withdraw, Deposit/Withdraw).
///
/// Replaces three `toggleBtn(active)` helpers that lived in ReserveForms, StakingVaultsPanel and
/// VaultDetailSection. Two were character-for-character identical; the third differed only in
/// padding, font size and a disabled state. That is one control that had been copied twice, not
/// three controls, so `size` and `disabled` cover every existing use.
export function ToggleButton({ active, onClick, disabled, size = 'sm', children, style = {}, ...rest }) {
  const lg = size === 'lg'
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      style={{
        flex: 1,
        padding: lg ? '12px 14px' : '8px 10px',
        borderRadius: T.r.md,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${active ? `${T.accent}88` : T.border}`,
        background: active ? T.accentDim : 'transparent',
        color: active ? T.accent : T.textMuted,
        fontFamily: T.fontM,
        fontSize: lg ? T.fs.body : T.fs.caption,
        fontWeight: 700,
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  )
}

/// Pill-shaped filter or view selector, sitting inline in a rail rather than filling a row.
///
/// Replaces `filterTab` (VaultsSection), `pillStyle` (PortfolioSection) and `subTabBtn`
/// (DetailPageShell). `DetailPageShell`'s other tab style stays where it is on purpose: it is an
/// UNDERLINE tab, a genuinely different control rather than a third copy of this one, and folding
/// it in here would merge two things that only look similar in a list of function names.
export function FilterPill({ active, onClick, size = 'md', children, style = {}, ...rest }) {
  const sm = size === 'sm'
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: sm ? '7px 14px' : '9px 16px',
        borderRadius: T.r.pill,
        cursor: 'pointer',
        border: `1px solid ${active ? `${T.accent}88` : T.border}`,
        background: active ? T.accentDim : 'rgba(255,255,255,0.02)',
        color: active ? T.accent : T.textMuted,
        fontFamily: T.fontM,
        fontSize: sm ? T.fs.caption : T.fs.body,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  )
}
